import { BannerPlatform } from '@app/graphql/types/graphql';
import CDNLink from '@app/utils/cdn-link';
import {
  Box,
  Dialog,
  Portal,
  CloseButton,
  IconButton,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  GridItem,
  Image,
} from '@chakra-ui/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslate } from '@tolgee/react';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface NewsBanner {
  id: string;
  title: string;
  description?: string | null;
  startTime: string;
  endTime: string;
  isActive: boolean;
  platforms: BannerPlatform[];
  imageUrl: string;
  allowPopup: boolean;
}

interface NewsBannerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  banners: NewsBanner[];
  selectedBannerIndex: number;
}

export default function NewsBannerDialog({ isOpen, onClose, banners, selectedBannerIndex }: NewsBannerDialogProps) {
  const { t } = useTranslate();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={() => {
        onClose();
      }}
      size="sm"
      placement="center"
      motionPreset="slide-in-bottom"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="6xl" maxH="6xl" className="news-dialog">
            <Dialog.Header py={4} borderBottom={'1px solid'} borderColor={'gray.200'} className="news-dialog__header">
              <Dialog.Title fontSize={'2xl'} fontWeight={'semibold'}>
                {t('news-banner.header', 'Hot news')}
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" className="news-dialog__close-btn" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body p={0} px={2} className="news-dialog__content">
              <Box position="relative" px={8}>
                <Swiper
                  modules={[Navigation, Pagination]}
                  spaceBetween={0}
                  slidesPerView={1}
                  loop={true}
                  initialSlide={selectedBannerIndex}
                  navigation={{
                    prevEl: '.dialog-swiper-button-prev',
                    nextEl: '.dialog-swiper-button-next',
                  }}
                  pagination={{
                    clickable: true,
                    dynamicBullets: true,
                  }}
                  style={{
                    paddingBottom: '30px',
                  }}
                >
                  {banners.map((banner) => (
                    <SwiperSlide key={banner.id}>
                      <VStack gap={6} p={6} align="stretch">
                        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6} alignItems="start">
                          <GridItem>
                            <Image
                              src={CDNLink(banner.imageUrl)}
                              alt={banner.title}
                              borderRadius="lg"
                              objectFit="cover"
                              width="100%"
                              maxH="400px"
                            />
                          </GridItem>

                          <GridItem>
                            <VStack align="start" gap={2} flex={1} mb={8}>
                              <Text fontSize="2xl" fontWeight="bold" lineHeight="shorter">
                                {banner.title}
                              </Text>
                              <HStack gap={4} wrap="wrap">
                                <Text fontSize="sm" color="gray.500">
                                  {formatDate(banner.startTime)} - {formatDate(banner.endTime)}
                                </Text>
                              </HStack>
                            </VStack>

                            <VStack align="start" gap={4} h="full">
                              <Text whiteSpace="pre-wrap" fontSize="md" color={'gray.600'} lineHeight="tall">
                                {banner.description}
                              </Text>
                            </VStack>
                          </GridItem>
                        </SimpleGrid>
                      </VStack>
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* Custom Navigation Buttons */}
                {banners.length > 1 && (
                  <>
                    <IconButton
                      className="dialog-swiper-button-prev"
                      aria-label="Previous banner"
                      position="absolute"
                      left={1}
                      top="48%"
                      transform="translateY(-50%)"
                      zIndex={10}
                      variant="outline"
                      bg="white"
                      border={'1px solid'}
                      borderColor={'gray.300'}
                      shadow="md"
                      size="lg"
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      className="dialog-swiper-button-next"
                      aria-label="Next banner"
                      position="absolute"
                      right={1}
                      top="48%"
                      transform="translateY(-50%)"
                      border={'1px solid'}
                      borderColor={'gray.300'}
                      zIndex={10}
                      variant="outline"
                      bg="white"
                      shadow="md"
                      size="lg"
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
